/**
 * Platform Capability Settings Components
 * Settings panels for SSO, Custom Domain, White Label, and API Access
 */

import React, { useState } from 'react';
import {
  Shield,
  Globe,
  Sparkles,
  Key,
  X,
  Copy,
  Eye,
  EyeOff,
  Check,
  AlertCircle,
  ExternalLink,
  RefreshCw,
  Plus,
  Trash2,
} from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '@/components/ui/dialog';
import { useAppTranslation } from '@/contexts/TranslationContext';
import { useFormattedDate } from '../hooks/useUserTimezone';

// ============================================================================
// SSO SETTINGS
// ============================================================================

interface SSOSettingsProps {
  isOpen: boolean;
  onClose: () => void;
  organizationId?: number;
}

export function SSOSettings({ isOpen, onClose, organizationId }: SSOSettingsProps) {
  const { t } = useAppTranslation();
  const [ssoEnabled, setSsoEnabled] = useState(false);
  const [ssoProvider, setSsoProvider] = useState<'saml' | 'oauth'>('saml');
  const [idpMetadataUrl, setIdpMetadataUrl] = useState('');
  const [entityId, setEntityId] = useState('');
  const [acsUrl] = useState('https://app.weavein.ai/auth/saml/callback');
  const [saving, setSaving] = useState(false);

  const handleSave = async () => {
    setSaving(true);
    try {
      // API call to save SSO settings
      console.log('Saving SSO settings:', { ssoEnabled, ssoProvider, idpMetadataUrl, entityId });
      // await apiClient.post(`/api/organizations/${organizationId}/sso`, {...})
      onClose();
    } catch (error) {
      console.error('Failed to save SSO settings:', error);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-[600px]">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Shield className="h-5 w-5 text-green-600" />
            {t('platform.capabilities.sso.title')}
          </DialogTitle>
          <DialogDescription>
            {t('platform.capabilities.sso.description')}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-6 py-4">
          {/* Enable SSO Toggle */}
          <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
            <div>
              <h4 className="font-medium text-gray-900">{t('platform.capabilities.sso.enableSso')}</h4>
              <p className="text-sm text-gray-500">{t('platform.capabilities.sso.enableSsoDesc')}</p>
            </div>
            <button
              onClick={() => setSsoEnabled(!ssoEnabled)}
              className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                ssoEnabled ? 'bg-blue-600' : 'bg-gray-200'
              }`}
            >
              <span
                className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                  ssoEnabled ? 'translate-x-6' : 'translate-x-1'
                }`}
              />
            </button>
          </div>

          {ssoEnabled && (
            <>
              {/* SSO Provider Selection */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  {t('platform.capabilities.sso.providerType')}
                </label>
                <div className="flex gap-4">
                  <button
                    onClick={() => setSsoProvider('saml')}
                    className={`flex-1 p-3 rounded-lg border text-center ${
                      ssoProvider === 'saml'
                        ? 'border-blue-500 bg-blue-50 text-blue-700'
                        : 'border-gray-200 hover:bg-gray-50'
                    }`}
                  >
                    <div className="font-medium">SAML 2.0</div>
                    <div className="text-xs text-gray-500">{t('platform.capabilities.sso.enterpriseIdp')}</div>
                  </button>
                  <button
                    onClick={() => setSsoProvider('oauth')}
                    className={`flex-1 p-3 rounded-lg border text-center ${
                      ssoProvider === 'oauth'
                        ? 'border-blue-500 bg-blue-50 text-blue-700'
                        : 'border-gray-200 hover:bg-gray-50'
                    }`}
                  >
                    <div className="font-medium">OAuth 2.0</div>
                    <div className="text-xs text-gray-500">{t('platform.capabilities.sso.openIdConnect')}</div>
                  </button>
                </div>
              </div>

              {ssoProvider === 'saml' && (
                <>
                  {/* Service Provider Info */}
                  <div className="p-4 bg-blue-50 rounded-lg">
                    <h4 className="font-medium text-blue-900 mb-2">{t('platform.capabilities.sso.serviceProviderDetails')}</h4>
                    <div className="space-y-2 text-sm">
                      <div className="flex items-center justify-between">
                        <span className="text-blue-700">{t('platform.capabilities.sso.acsUrl')}:</span>
                        <code className="bg-white px-2 py-1 rounded text-blue-900">{acsUrl}</code>
                      </div>
                      <div className="flex items-center justify-between">
                        <span className="text-blue-700">{t('platform.capabilities.sso.entityId')}:</span>
                        <code className="bg-white px-2 py-1 rounded text-blue-900">
                          urn:weavein:sp:{organizationId || 'org'}
                        </code>
                      </div>
                    </div>
                  </div>

                  {/* IDP Metadata URL */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      {t('platform.capabilities.sso.idpMetadataUrl')}
                    </label>
                    <input
                      type="url"
                      value={idpMetadataUrl}
                      onChange={(e) => setIdpMetadataUrl(e.target.value)}
                      placeholder="https://your-idp.com/metadata.xml"
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    />
                    <p className="text-xs text-gray-500 mt-1">
                      {t('platform.capabilities.sso.idpMetadataUrlDesc')}
                    </p>
                  </div>

                  {/* Entity ID Override */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      {t('platform.capabilities.sso.customEntityId')}
                    </label>
                    <input
                      type="text"
                      value={entityId}
                      onChange={(e) => setEntityId(e.target.value)}
                      placeholder={t('platform.capabilities.sso.customEntityIdPlaceholder')}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    />
                  </div>
                </>
              )}

              {ssoProvider === 'oauth' && (
                <>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      {t('platform.capabilities.sso.oauthClientId')}
                    </label>
                    <input
                      type="text"
                      placeholder={t('platform.capabilities.sso.oauthClientIdPlaceholder')}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      {t('platform.capabilities.sso.oauthClientSecret')}
                    </label>
                    <input
                      type="password"
                      placeholder={t('platform.capabilities.sso.oauthClientSecretPlaceholder')}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      {t('platform.capabilities.sso.authorizationUrl')}
                    </label>
                    <input
                      type="url"
                      placeholder="https://your-idp.com/oauth/authorize"
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      {t('platform.capabilities.sso.tokenUrl')}
                    </label>
                    <input
                      type="url"
                      placeholder="https://your-idp.com/oauth/token"
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    />
                  </div>
                </>
              )}
            </>
          )}
        </div>

        <DialogFooter>
          <button
            onClick={onClose}
            className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50"
          >
            {t('platform.capabilities.buttons.cancel')}
          </button>
          <button
            onClick={handleSave}
            disabled={saving}
            className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50"
          >
            {saving ? t('platform.capabilities.buttons.saving') : t('platform.capabilities.buttons.saveConfiguration')}
          </button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ============================================================================
// CUSTOM DOMAIN SETTINGS
// ============================================================================

interface DomainSettingsProps {
  isOpen: boolean;
  onClose: () => void;
  organizationId?: number;
}

export function DomainSettings({ isOpen, onClose, organizationId }: DomainSettingsProps) {
  const { t } = useAppTranslation();
  const [domain, setDomain] = useState('');
  const [verificationStatus, setVerificationStatus] = useState<'pending' | 'verified' | 'failed' | null>(null);
  const [sslStatus, setSslStatus] = useState<'pending' | 'active' | 'failed' | null>(null);
  const [saving, setSaving] = useState(false);
  const [verifying, setVerifying] = useState(false);

  const handleAddDomain = async () => {
    setSaving(true);
    try {
      // API call to add domain
      console.log('Adding domain:', domain);
      setVerificationStatus('pending');
    } catch (error) {
      console.error('Failed to add domain:', error);
    } finally {
      setSaving(false);
    }
  };

  const handleVerify = async () => {
    setVerifying(true);
    try {
      // API call to verify domain
      console.log('Verifying domain:', domain);
      // Simulate verification
      setTimeout(() => {
        setVerificationStatus('verified');
        setSslStatus('active');
        setVerifying(false);
      }, 2000);
    } catch (error) {
      console.error('Failed to verify domain:', error);
      setVerificationStatus('failed');
      setVerifying(false);
    }
  };

  const dnsRecords = [
    { type: 'CNAME', name: domain || 'app.yourdomain.com', value: 'proxy.weavein.ai', purpose: 'App routing' },
    { type: 'TXT', name: `_weavein.${domain || 'yourdomain.com'}`, value: 'weavein-verify=abc123xyz', purpose: 'Ownership verification' },
  ];

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-[650px]">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Globe className="h-5 w-5 text-orange-600" />
            {t('platform.capabilities.domain.title')}
          </DialogTitle>
          <DialogDescription>
            {t('platform.capabilities.domain.description')}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-6 py-4">
          {/* Domain Input */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              {t('platform.capabilities.domain.customDomain')}
            </label>
            <div className="flex gap-2">
              <input
                type="text"
                value={domain}
                onChange={(e) => setDomain(e.target.value)}
                placeholder="app.yourdomain.com"
                className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
              <button
                onClick={handleAddDomain}
                disabled={!domain || saving}
                className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50"
              >
                {saving ? t('platform.capabilities.domain.adding') : t('platform.capabilities.domain.addDomain')}
              </button>
            </div>
          </div>

          {verificationStatus && (
            <>
              {/* Status Cards */}
              <div className="grid grid-cols-2 gap-4">
                <div className={`p-4 rounded-lg border ${
                  verificationStatus === 'verified' ? 'bg-green-50 border-green-200' :
                  verificationStatus === 'failed' ? 'bg-red-50 border-red-200' :
                  'bg-yellow-50 border-yellow-200'
                }`}>
                  <div className="flex items-center gap-2 mb-1">
                    {verificationStatus === 'verified' ? (
                      <Check className="h-4 w-4 text-green-600" />
                    ) : verificationStatus === 'failed' ? (
                      <AlertCircle className="h-4 w-4 text-red-600" />
                    ) : (
                      <RefreshCw className="h-4 w-4 text-yellow-600" />
                    )}
                    <span className="font-medium text-gray-900">{t('platform.capabilities.domain.dnsVerification')}</span>
                  </div>
                  <p className="text-sm text-gray-600">
                    {verificationStatus === 'verified' ? t('platform.capabilities.domain.domainVerified') :
                     verificationStatus === 'failed' ? t('platform.capabilities.domain.verificationFailed') :
                     t('platform.capabilities.domain.pendingVerification')}
                  </p>
                </div>

                <div className={`p-4 rounded-lg border ${
                  sslStatus === 'active' ? 'bg-green-50 border-green-200' :
                  sslStatus === 'failed' ? 'bg-red-50 border-red-200' :
                  'bg-gray-50 border-gray-200'
                }`}>
                  <div className="flex items-center gap-2 mb-1">
                    {sslStatus === 'active' ? (
                      <Check className="h-4 w-4 text-green-600" />
                    ) : sslStatus === 'failed' ? (
                      <AlertCircle className="h-4 w-4 text-red-600" />
                    ) : (
                      <Shield className="h-4 w-4 text-gray-400" />
                    )}
                    <span className="font-medium text-gray-900">{t('platform.capabilities.domain.sslCertificate')}</span>
                  </div>
                  <p className="text-sm text-gray-600">
                    {sslStatus === 'active' ? t('platform.capabilities.domain.certificateActive') :
                     sslStatus === 'failed' ? t('platform.capabilities.domain.certificateFailed') :
                     t('platform.capabilities.domain.awaitingVerification')}
                  </p>
                </div>
              </div>

              {/* DNS Records */}
              <div>
                <h4 className="font-medium text-gray-900 mb-2">{t('platform.capabilities.domain.requiredDnsRecords')}</h4>
                <div className="bg-gray-50 rounded-lg p-4 space-y-3">
                  {dnsRecords.map((record, index) => (
                    <div key={index} className="text-sm">
                      <div className="flex items-center gap-2 mb-1">
                        <span className="px-2 py-0.5 bg-gray-200 rounded text-xs font-mono">
                          {record.type}
                        </span>
                        <span className="text-gray-500">{record.purpose}</span>
                      </div>
                      <div className="grid grid-cols-2 gap-2 text-xs">
                        <div>
                          <span className="text-gray-500">Name:</span>
                          <code className="ml-1 bg-white px-1 rounded">{record.name}</code>
                        </div>
                        <div>
                          <span className="text-gray-500">Value:</span>
                          <code className="ml-1 bg-white px-1 rounded">{record.value}</code>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Verify Button */}
              {verificationStatus !== 'verified' && (
                <button
                  onClick={handleVerify}
                  disabled={verifying}
                  className="w-full py-2 border border-blue-600 text-blue-600 rounded-lg hover:bg-blue-50 disabled:opacity-50 flex items-center justify-center gap-2"
                >
                  {verifying ? (
                    <>
                      <RefreshCw className="h-4 w-4 animate-spin" />
                      {t('platform.capabilities.domain.verifying')}
                    </>
                  ) : (
                    <>
                      <RefreshCw className="h-4 w-4" />
                      {t('platform.capabilities.domain.checkDnsVerify')}
                    </>
                  )}
                </button>
              )}
            </>
          )}
        </div>

        <DialogFooter>
          <button
            onClick={onClose}
            className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50"
          >
            {t('platform.capabilities.buttons.close')}
          </button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ============================================================================
// WHITE LABEL / BRANDING SETTINGS
// ============================================================================

interface BrandingSettingsProps {
  isOpen: boolean;
  onClose: () => void;
  organizationId?: number;
}

export function BrandingSettings({ isOpen, onClose, organizationId }: BrandingSettingsProps) {
  const { t } = useAppTranslation();
  const [brandName, setBrandName] = useState('');
  const [primaryColor, setPrimaryColor] = useState('#3B82F6');
  const [logoUrl, setLogoUrl] = useState('');
  const [faviconUrl, setFaviconUrl] = useState('');
  const [customCss, setCustomCss] = useState('');
  const [saving, setSaving] = useState(false);

  const handleSave = async () => {
    setSaving(true);
    try {
      console.log('Saving branding settings:', { brandName, primaryColor, logoUrl, faviconUrl, customCss });
      onClose();
    } catch (error) {
      console.error('Failed to save branding settings:', error);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-[600px]">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Sparkles className="h-5 w-5 text-pink-600" />
            {t('platform.capabilities.branding.title')}
          </DialogTitle>
          <DialogDescription>
            {t('platform.capabilities.branding.description')}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-6 py-4">
          {/* Brand Name */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              {t('platform.capabilities.branding.brandName')}
            </label>
            <input
              type="text"
              value={brandName}
              onChange={(e) => setBrandName(e.target.value)}
              placeholder={t('platform.capabilities.branding.brandNamePlaceholder')}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            />
            <p className="text-xs text-gray-500 mt-1">
              {t('platform.capabilities.branding.brandNameDesc')}
            </p>
          </div>

          {/* Primary Color */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              {t('platform.capabilities.branding.primaryColor')}
            </label>
            <div className="flex gap-3">
              <input
                type="color"
                value={primaryColor}
                onChange={(e) => setPrimaryColor(e.target.value)}
                className="h-10 w-14 rounded cursor-pointer"
              />
              <input
                type="text"
                value={primaryColor}
                onChange={(e) => setPrimaryColor(e.target.value)}
                className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent font-mono"
              />
            </div>
          </div>

          {/* Logo URL */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              {t('platform.capabilities.branding.logoUrl')}
            </label>
            <input
              type="url"
              value={logoUrl}
              onChange={(e) => setLogoUrl(e.target.value)}
              placeholder="https://yourdomain.com/logo.png"
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            />
            <p className="text-xs text-gray-500 mt-1">
              {t('platform.capabilities.branding.logoUrlDesc')}
            </p>
          </div>

          {/* Favicon URL */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              {t('platform.capabilities.branding.faviconUrl')}
            </label>
            <input
              type="url"
              value={faviconUrl}
              onChange={(e) => setFaviconUrl(e.target.value)}
              placeholder="https://yourdomain.com/favicon.ico"
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            />
          </div>

          {/* Custom CSS */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              {t('platform.capabilities.branding.customCss')}
            </label>
            <textarea
              value={customCss}
              onChange={(e) => setCustomCss(e.target.value)}
              placeholder="/* Add custom CSS overrides here */"
              rows={4}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent font-mono text-sm"
            />
            <p className="text-xs text-gray-500 mt-1">
              {t('platform.capabilities.branding.customCssDesc')}
            </p>
          </div>

          {/* Preview */}
          <div className="p-4 bg-gray-50 rounded-lg">
            <h4 className="font-medium text-gray-900 mb-2">{t('platform.capabilities.branding.preview')}</h4>
            <div className="flex items-center gap-3 p-3 bg-white rounded border">
              {logoUrl ? (
                <img src={logoUrl} alt="Logo preview" className="h-8 object-contain" />
              ) : (
                <div
                  className="h-8 w-24 rounded flex items-center justify-center text-white text-sm font-bold"
                  style={{ backgroundColor: primaryColor }}
                >
                  {brandName || 'Logo'}
                </div>
              )}
              <span className="font-semibold" style={{ color: primaryColor }}>
                {brandName || 'Your Brand'}
              </span>
            </div>
          </div>
        </div>

        <DialogFooter>
          <button
            onClick={onClose}
            className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50"
          >
            {t('platform.capabilities.buttons.cancel')}
          </button>
          <button
            onClick={handleSave}
            disabled={saving}
            className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50"
          >
            {saving ? t('platform.capabilities.buttons.saving') : t('platform.capabilities.branding.saveBranding')}
          </button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ============================================================================
// API ACCESS SETTINGS
// ============================================================================

interface APIKey {
  id: string;
  name: string;
  prefix: string;
  createdAt: string;
  lastUsed: string | null;
  scopes: string[];
}

interface APISettingsProps {
  isOpen: boolean;
  onClose: () => void;
  organizationId?: number;
}

export function APISettings({ isOpen, onClose, organizationId }: APISettingsProps) {
  const { t } = useAppTranslation();
  const { fmt } = useFormattedDate();
  const [apiKeys, setApiKeys] = useState<APIKey[]>([
    {
      id: '1',
      name: 'Production API Key',
      prefix: 'wv_prod_',
      createdAt: '2024-12-01T10:00:00Z',
      lastUsed: '2025-01-03T15:30:00Z',
      scopes: ['read', 'write'],
    },
    {
      id: '2',
      name: 'Development Key',
      prefix: 'wv_dev_',
      createdAt: '2024-12-15T10:00:00Z',
      lastUsed: null,
      scopes: ['read'],
    },
  ]);
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [newKeyName, setNewKeyName] = useState('');
  const [selectedScopes, setSelectedScopes] = useState<string[]>(['read']);
  const [newKey, setNewKey] = useState<string | null>(null);
  const [showKey, setShowKey] = useState(false);
  const [copied, setCopied] = useState(false);

  const handleCreateKey = async () => {
    // Simulate API key generation
    const generatedKey = `wv_${Math.random().toString(36).substring(2, 10)}_${Math.random().toString(36).substring(2, 26)}`;
    setNewKey(generatedKey);

    const newApiKey: APIKey = {
      id: String(Date.now()),
      name: newKeyName,
      prefix: generatedKey.substring(0, 8),
      createdAt: new Date().toISOString(),
      lastUsed: null,
      scopes: selectedScopes,
    };

    setApiKeys([...apiKeys, newApiKey]);
    setShowCreateDialog(false);
    setNewKeyName('');
    setSelectedScopes(['read']);
  };

  const handleDeleteKey = (keyId: string) => {
    setApiKeys(apiKeys.filter(k => k.id !== keyId));
  };

  const copyToClipboard = (text: string) => {
    navigator.clipboard.writeText(text);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const formatDate = (dateString: string) => {
    return fmt.medium(dateString);
  };

  const availableScopes = [
    { id: 'read', label: t('platform.capabilities.api.scopeRead'), description: t('platform.capabilities.api.scopeReadDesc') },
    { id: 'write', label: t('platform.capabilities.api.scopeWrite'), description: t('platform.capabilities.api.scopeWriteDesc') },
    { id: 'delete', label: t('platform.capabilities.api.scopeDelete'), description: t('platform.capabilities.api.scopeDeleteDesc') },
    { id: 'admin', label: t('platform.capabilities.api.scopeAdmin'), description: t('platform.capabilities.api.scopeAdminDesc') },
  ];

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-[700px]">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Key className="h-5 w-5 text-blue-600" />
            {t('platform.capabilities.api.title')}
          </DialogTitle>
          <DialogDescription>
            {t('platform.capabilities.api.description')}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-6 py-4">
          {/* New Key Display */}
          {newKey && (
            <div className="p-4 bg-green-50 border border-green-200 rounded-lg">
              <div className="flex items-center gap-2 mb-2">
                <Check className="h-5 w-5 text-green-600" />
                <h4 className="font-medium text-green-900">{t('platform.capabilities.api.keyGenerated')}</h4>
              </div>
              <p className="text-sm text-green-700 mb-3">
                {t('platform.capabilities.api.keyGeneratedWarning')}
              </p>
              <div className="flex items-center gap-2">
                <code className="flex-1 p-2 bg-white rounded border font-mono text-sm overflow-x-auto">
                  {showKey ? newKey : '•'.repeat(40)}
                </code>
                <button
                  onClick={() => setShowKey(!showKey)}
                  className="p-2 text-gray-500 hover:text-gray-700"
                >
                  {showKey ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                </button>
                <button
                  onClick={() => copyToClipboard(newKey)}
                  className="p-2 text-gray-500 hover:text-gray-700"
                >
                  {copied ? <Check className="h-4 w-4 text-green-600" /> : <Copy className="h-4 w-4" />}
                </button>
              </div>
            </div>
          )}

          {/* API Keys List */}
          <div>
            <div className="flex items-center justify-between mb-3">
              <h4 className="font-medium text-gray-900">{t('platform.capabilities.api.apiKeys')}</h4>
              <button
                onClick={() => setShowCreateDialog(true)}
                className="flex items-center gap-1 text-sm text-blue-600 hover:text-blue-700"
              >
                <Plus className="h-4 w-4" />
                {t('platform.capabilities.api.createNewKey')}
              </button>
            </div>

            <div className="border rounded-lg divide-y">
              {apiKeys.map((key) => (
                <div key={key.id} className="p-4 flex items-center justify-between">
                  <div>
                    <div className="font-medium text-gray-900">{key.name}</div>
                    <div className="text-sm text-gray-500">
                      <code className="bg-gray-100 px-1 rounded">{key.prefix}...</code>
                      <span className="mx-2">•</span>
                      Created {formatDate(key.createdAt)}
                      {key.lastUsed && (
                        <>
                          <span className="mx-2">•</span>
                          Last used {formatDate(key.lastUsed)}
                        </>
                      )}
                    </div>
                    <div className="flex gap-1 mt-1">
                      {key.scopes.map((scope) => (
                        <span
                          key={scope}
                          className="px-2 py-0.5 bg-blue-100 text-blue-700 text-xs rounded-full"
                        >
                          {scope}
                        </span>
                      ))}
                    </div>
                  </div>
                  <button
                    onClick={() => handleDeleteKey(key.id)}
                    className="p-2 text-red-500 hover:text-red-700 hover:bg-red-50 rounded"
                  >
                    <Trash2 className="h-4 w-4" />
                  </button>
                </div>
              ))}
            </div>
          </div>

          {/* Create Key Dialog */}
          {showCreateDialog && (
            <div className="p-4 bg-gray-50 rounded-lg border">
              <h4 className="font-medium text-gray-900 mb-4">{t('platform.capabilities.api.createNewApiKey')}</h4>
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    {t('platform.capabilities.api.keyName')}
                  </label>
                  <input
                    type="text"
                    value={newKeyName}
                    onChange={(e) => setNewKeyName(e.target.value)}
                    placeholder={t('platform.capabilities.api.keyNamePlaceholder')}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    {t('platform.capabilities.api.scopes')}
                  </label>
                  <div className="grid grid-cols-2 gap-2">
                    {availableScopes.map((scope) => (
                      <label
                        key={scope.id}
                        className={`p-3 border rounded-lg cursor-pointer ${
                          selectedScopes.includes(scope.id)
                            ? 'border-blue-500 bg-blue-50'
                            : 'border-gray-200 hover:bg-gray-50'
                        }`}
                      >
                        <div className="flex items-center gap-2">
                          <input
                            type="checkbox"
                            checked={selectedScopes.includes(scope.id)}
                            onChange={(e) => {
                              if (e.target.checked) {
                                setSelectedScopes([...selectedScopes, scope.id]);
                              } else {
                                setSelectedScopes(selectedScopes.filter(s => s !== scope.id));
                              }
                            }}
                            className="rounded"
                          />
                          <div>
                            <div className="font-medium text-sm">{scope.label}</div>
                            <div className="text-xs text-gray-500">{scope.description}</div>
                          </div>
                        </div>
                      </label>
                    ))}
                  </div>
                </div>
                <div className="flex gap-2 justify-end">
                  <button
                    onClick={() => setShowCreateDialog(false)}
                    className="px-3 py-1.5 text-sm border border-gray-300 rounded hover:bg-gray-50"
                  >
                    {t('platform.capabilities.buttons.cancel')}
                  </button>
                  <button
                    onClick={handleCreateKey}
                    disabled={!newKeyName || selectedScopes.length === 0}
                    className="px-3 py-1.5 text-sm bg-blue-600 text-white rounded hover:bg-blue-700 disabled:opacity-50"
                  >
                    {t('platform.capabilities.api.generateKey')}
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* API Documentation Link */}
          <div className="p-4 bg-gray-50 rounded-lg flex items-center justify-between">
            <div>
              <h4 className="font-medium text-gray-900">{t('platform.capabilities.api.documentation')}</h4>
              <p className="text-sm text-gray-500">{t('platform.capabilities.api.documentationDesc')}</p>
            </div>
            <a
              href="/docs/api"
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center gap-1 text-blue-600 hover:text-blue-700"
            >
              {t('platform.capabilities.api.viewDocs')}
              <ExternalLink className="h-4 w-4" />
            </a>
          </div>
        </div>

        <DialogFooter>
          <button
            onClick={onClose}
            className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50"
          >
            {t('platform.capabilities.buttons.close')}
          </button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ============================================================================
// WEBHOOKS SETTINGS
// ============================================================================

interface WebhooksSettingsProps {
  isOpen: boolean;
  onClose: () => void;
  organizationId?: number;
}

interface WebhookEndpoint {
  id: string;
  url: string;
  events: string[];
  status: 'active' | 'inactive' | 'failed';
  lastTriggered?: string;
  secret: string;
}

export function WebhooksSettings({ isOpen, onClose, organizationId }: WebhooksSettingsProps) {
  const { fmt } = useFormattedDate();
  const { t } = useAppTranslation();
  const [webhooksEnabled, setWebhooksEnabled] = useState(true);
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [newWebhookUrl, setNewWebhookUrl] = useState('');
  const [selectedEvents, setSelectedEvents] = useState<string[]>([]);
  const [endpoints, setEndpoints] = useState<WebhookEndpoint[]>([
    {
      id: '1',
      url: 'https://api.example.com/webhooks/weavein',
      events: ['member.created', 'member.updated'],
      status: 'active',
      lastTriggered: '2025-01-05T10:30:00Z',
      secret: 'whsec_xxxxxxxxxxxxxxxxxxxxxxxxxxxx',
    },
  ]);

  const availableEvents = [
    { id: 'member.created', label: 'Member Created', description: 'When a new member is added' },
    { id: 'member.updated', label: 'Member Updated', description: 'When member data changes' },
    { id: 'referral.created', label: 'Referral Created', description: 'When a new referral is created' },
    { id: 'referral.matched', label: 'Referral Matched', description: 'When a referral is matched to a provider' },
    { id: 'enrollment.created', label: 'Enrollment Created', description: 'When a member is enrolled' },
    { id: 'enrollment.completed', label: 'Enrollment Completed', description: 'When enrollment is completed' },
    { id: 'form.submitted', label: 'Form Submitted', description: 'When a form is submitted' },
    { id: 'milestone.achieved', label: 'Milestone Achieved', description: 'When a member achieves a milestone' },
  ];

  const handleCreateEndpoint = () => {
    if (!newWebhookUrl || selectedEvents.length === 0) return;

    const newEndpoint: WebhookEndpoint = {
      id: Date.now().toString(),
      url: newWebhookUrl,
      events: selectedEvents,
      status: 'active',
      secret: 'whsec_' + Math.random().toString(36).substring(2, 30),
    };

    setEndpoints([...endpoints, newEndpoint]);
    setNewWebhookUrl('');
    setSelectedEvents([]);
    setShowCreateDialog(false);
  };

  const handleDeleteEndpoint = (id: string) => {
    setEndpoints(endpoints.filter(e => e.id !== id));
  };

  const handleToggleEndpoint = (id: string) => {
    setEndpoints(endpoints.map(e =>
      e.id === id ? { ...e, status: e.status === 'active' ? 'inactive' : 'active' } : e
    ));
  };

  const getStatusBadge = (status: WebhookEndpoint['status']) => {
    switch (status) {
      case 'active':
        return <span className="px-2 py-0.5 text-xs font-medium bg-green-100 text-green-700 rounded-full">{t('platform.capabilities.webhooks.statusActive')}</span>;
      case 'inactive':
        return <span className="px-2 py-0.5 text-xs font-medium bg-gray-100 text-gray-700 rounded-full">{t('platform.capabilities.webhooks.statusInactive')}</span>;
      case 'failed':
        return <span className="px-2 py-0.5 text-xs font-medium bg-red-100 text-red-700 rounded-full">{t('platform.capabilities.webhooks.statusFailed')}</span>;
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-[700px] max-h-[80vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <RefreshCw className="h-5 w-5 text-purple-600" />
            {t('platform.capabilities.webhooks.title')}
          </DialogTitle>
          <DialogDescription>
            {t('platform.capabilities.webhooks.description')}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-6 py-4">
          {/* Enable Webhooks Toggle */}
          <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
            <div>
              <h4 className="font-medium text-gray-900">{t('platform.capabilities.webhooks.enableWebhooks')}</h4>
              <p className="text-sm text-gray-500">{t('platform.capabilities.webhooks.enableWebhooksDesc')}</p>
            </div>
            <button
              onClick={() => setWebhooksEnabled(!webhooksEnabled)}
              className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                webhooksEnabled ? 'bg-blue-600' : 'bg-gray-200'
              }`}
            >
              <span
                className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                  webhooksEnabled ? 'translate-x-6' : 'translate-x-1'
                }`}
              />
            </button>
          </div>

          {webhooksEnabled && (
            <>
              {/* Webhook Endpoints */}
              <div className="space-y-4">
                <div className="flex items-center justify-between">
                  <h4 className="font-medium text-gray-900">{t('platform.capabilities.webhooks.endpoints')}</h4>
                  <button
                    onClick={() => setShowCreateDialog(true)}
                    className="flex items-center gap-1 px-3 py-1.5 text-sm bg-blue-600 text-white rounded-lg hover:bg-blue-700"
                  >
                    <Plus className="h-4 w-4" />
                    {t('platform.capabilities.webhooks.addEndpoint')}
                  </button>
                </div>

                {/* Existing Endpoints */}
                <div className="space-y-3">
                  {endpoints.length === 0 ? (
                    <div className="p-4 text-center text-gray-500 border border-dashed border-gray-300 rounded-lg">
                      {t('platform.capabilities.webhooks.noEndpoints')}
                    </div>
                  ) : (
                    endpoints.map((endpoint) => (
                      <div key={endpoint.id} className="p-4 border border-gray-200 rounded-lg">
                        <div className="flex items-start justify-between mb-3">
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2 mb-1">
                              <code className="text-sm font-mono bg-gray-100 px-2 py-0.5 rounded truncate max-w-[400px]">
                                {endpoint.url}
                              </code>
                              {getStatusBadge(endpoint.status)}
                            </div>
                            <div className="text-xs text-gray-500">
                              Events: {endpoint.events.join(', ')}
                            </div>
                            {endpoint.lastTriggered && (
                              <div className="text-xs text-gray-400 mt-1">
                                Last triggered: {fmt.dateTime(endpoint.lastTriggered)}
                              </div>
                            )}
                          </div>
                          <div className="flex items-center gap-2 ml-4">
                            <button
                              onClick={() => handleToggleEndpoint(endpoint.id)}
                              className={`px-3 py-1 text-xs rounded ${
                                endpoint.status === 'active'
                                  ? 'bg-yellow-100 text-yellow-700 hover:bg-yellow-200'
                                  : 'bg-green-100 text-green-700 hover:bg-green-200'
                              }`}
                            >
                              {endpoint.status === 'active' ? t('platform.capabilities.webhooks.disable') : t('platform.capabilities.webhooks.enable')}
                            </button>
                            <button
                              onClick={() => handleDeleteEndpoint(endpoint.id)}
                              className="p-2 text-red-500 hover:text-red-700 hover:bg-red-50 rounded"
                            >
                              <Trash2 className="h-4 w-4" />
                            </button>
                          </div>
                        </div>

                        {/* Secret Key */}
                        <div className="mt-3 p-2 bg-gray-50 rounded flex items-center justify-between">
                          <div className="text-xs text-gray-600">
                            <span className="font-medium">Signing Secret:</span>{' '}
                            <code className="font-mono">{endpoint.secret.substring(0, 20)}...</code>
                          </div>
                          <button
                            onClick={() => navigator.clipboard.writeText(endpoint.secret)}
                            className="p-1 text-gray-500 hover:text-gray-700"
                          >
                            <Copy className="h-3 w-3" />
                          </button>
                        </div>
                      </div>
                    ))
                  )}
                </div>
              </div>

              {/* Create Endpoint Dialog */}
              {showCreateDialog && (
                <div className="p-4 bg-gray-50 rounded-lg border">
                  <h4 className="font-medium text-gray-900 mb-4">{t('platform.capabilities.webhooks.addWebhookEndpoint')}</h4>
                  <div className="space-y-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        {t('platform.capabilities.webhooks.endpointUrl')}
                      </label>
                      <input
                        type="url"
                        value={newWebhookUrl}
                        onChange={(e) => setNewWebhookUrl(e.target.value)}
                        placeholder="https://api.example.com/webhooks"
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        {t('platform.capabilities.webhooks.eventsToSend')}
                      </label>
                      <div className="grid grid-cols-2 gap-2 max-h-48 overflow-y-auto">
                        {availableEvents.map((event) => (
                          <label
                            key={event.id}
                            className={`p-3 border rounded-lg cursor-pointer ${
                              selectedEvents.includes(event.id)
                                ? 'border-blue-500 bg-blue-50'
                                : 'border-gray-200 hover:bg-gray-50'
                            }`}
                          >
                            <div className="flex items-center gap-2">
                              <input
                                type="checkbox"
                                checked={selectedEvents.includes(event.id)}
                                onChange={(e) => {
                                  if (e.target.checked) {
                                    setSelectedEvents([...selectedEvents, event.id]);
                                  } else {
                                    setSelectedEvents(selectedEvents.filter(s => s !== event.id));
                                  }
                                }}
                                className="rounded"
                              />
                              <div>
                                <div className="font-medium text-sm">{event.label}</div>
                                <div className="text-xs text-gray-500">{event.description}</div>
                              </div>
                            </div>
                          </label>
                        ))}
                      </div>
                    </div>
                    <div className="flex gap-2 justify-end">
                      <button
                        onClick={() => setShowCreateDialog(false)}
                        className="px-3 py-1.5 text-sm border border-gray-300 rounded hover:bg-gray-50"
                      >
                        {t('platform.capabilities.buttons.cancel')}
                      </button>
                      <button
                        onClick={handleCreateEndpoint}
                        disabled={!newWebhookUrl || selectedEvents.length === 0}
                        className="px-3 py-1.5 text-sm bg-blue-600 text-white rounded hover:bg-blue-700 disabled:opacity-50"
                      >
                        {t('platform.capabilities.webhooks.createEndpoint')}
                      </button>
                    </div>
                  </div>
                </div>
              )}

              {/* Webhook Documentation */}
              <div className="p-4 bg-gray-50 rounded-lg flex items-center justify-between">
                <div>
                  <h4 className="font-medium text-gray-900">{t('platform.capabilities.webhooks.documentation')}</h4>
                  <p className="text-sm text-gray-500">{t('platform.capabilities.webhooks.documentationDesc')}</p>
                </div>
                <a
                  href="/docs/webhooks"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center gap-1 text-blue-600 hover:text-blue-700"
                >
                  {t('platform.capabilities.webhooks.viewDocs')}
                  <ExternalLink className="h-4 w-4" />
                </a>
              </div>
            </>
          )}
        </div>

        <DialogFooter>
          <button
            onClick={onClose}
            className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50"
          >
            {t('platform.capabilities.buttons.close')}
          </button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default {
  SSOSettings,
  DomainSettings,
  BrandingSettings,
  APISettings,
  WebhooksSettings,
};
